/**
 * Miscellaneous UI-related redux state.
 *
 * Sometimes used for general actions and state
 * when setting up prop inheritance is cumbersome.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "../store"

// Modal config carries caller-specific fields (title, okFn, cancelFn, ...); kept permissive
type ConfirmModal = any

interface UiState {
	confirmModal: ConfirmModal
	leftPanelVisible: boolean
	modalIsActive: boolean
	testResultMergeToggle: boolean
	eventDetailEditDisabled: boolean
	openDropdowns: Record<string, boolean>
	memberCategoryTab: string | null
	disableRoleSelect: boolean
	// Stores a progress fraction (number) or a boolean flag depending on caller
	globalLoadingWithProgress: number | boolean
	eventForceReload: boolean
	popups: unknown[]
}

const initialState: UiState = {
	confirmModal: {},
	leftPanelVisible: false,
	modalIsActive: false, // Whether a modal is being currently shown
	testResultMergeToggle: true,
	eventDetailEditDisabled: false,
	openDropdowns: {},
	memberCategoryTab: null,
	disableRoleSelect: false,
	globalLoadingWithProgress: false,
	eventForceReload: false, // Forces refetch of event modal; needed due to video upload not invalidating tags properly due to in-flight requests
	popups: [], // Contains youtube video popups used in help page (needs to available in every page)
}

export const uiSlice = createSlice({
	name: "ui",
	initialState,
	reducers: {
		toggleLeftPanel(state) {
			state.leftPanelVisible = !state.leftPanelVisible
		},
		toggleDropdownOpen(state, { payload: dropdownId }: PayloadAction<string | undefined>) {
			if (!dropdownId) return
			const currentValue = state.openDropdowns[dropdownId] ?? false
			state.openDropdowns[dropdownId] = !currentValue
		},
		setConfirmModal(state, { payload }: PayloadAction<ConfirmModal>) {
			if (!("show" in payload)) {
				payload.show = true
			}
			state.confirmModal = payload
		},
		setConfirmModalVisibility(state, { payload }: PayloadAction<boolean>) {
			state.confirmModal.show = payload
		},
		setModalIsActive(state, { payload }: PayloadAction<boolean>) {
			state.modalIsActive = payload
		},
		setTestResultMergeToggle(state, { payload }: PayloadAction<boolean>) {
			state.testResultMergeToggle = payload
		},
		setEventDetailEditDisable(state, { payload }: PayloadAction<boolean>) {
			state.eventDetailEditDisabled = payload
		},
		setDisableRoleSelect(state, { payload }: PayloadAction<boolean>) {
			state.disableRoleSelect = payload
		},
		globalLoadingWithProgress(state, { payload }: PayloadAction<number | boolean>) {
			state.globalLoadingWithProgress = payload
		},
		setEventForceReload(state, { payload }: PayloadAction<boolean>) {
			state.eventForceReload = payload
		},
		setPopups(state, { payload }: PayloadAction<unknown[]>) {
			state.popups = payload
		},
	},
})

export const {
	setConfirmModal,
	setConfirmModalVisibility,
	toggleLeftPanel,
	setModalIsActive,
	setTestResultMergeToggle,
	setEventDetailEditDisable,
	setDisableRoleSelect,
	globalLoadingWithProgress,
	setEventForceReload,
	setPopups,
	toggleDropdownOpen,
} = uiSlice.actions

export const uiSelector = (state: RootState) => state.ui
export const confirmModalSelector = (state: RootState) => state.ui.confirmModal
export const leftPanelVisibilitySelector = (state: RootState) => state.ui.leftPanelVisible
export const globalLoadingWithProgressSelector = (state: RootState) => state.ui.globalLoadingWithProgress
